import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  TextField,
  IconButton,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper
} from '@material-ui/core';
import { parse, format, isValid } from 'date-fns';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import PersonIcon from '@material-ui/icons/Person';

import { useRequest } from 'src/hooks';
import { useSelector } from 'react-redux';
import { useNotif } from 'src/hooks';
import SelectTime from 'src/components/SelectTime';

// REMARKS: sa admin side lahat ng col meron, ang pending is pano mo i show yung may mga request na col lang
const formatDate = dateStr => {
  if (!dateStr) return 'Invalid date';

  const parsed = parse(String(dateStr), 'yyyyMMdd', new Date());

  if (!isValid(parsed)) return 'Invalid date';

  return format(parsed, 'MMM dd, yyyy');
};

const parseTimeStringToDate = timeStr => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  const d = new Date();
  d.setHours(h);
  d.setMinutes(m);
  d.setSeconds(0);
  return d;
};

const RequestLogs = ({
  value,
  colIndex,
  item,
  col,
  portalShowEmpLogs = false,
  handleReqTime = () => {},
  handleHrRemarks = () => {},
  setOpenRequestLogs = false,
  prevRemarks,
  setPrevRemarks,
  shownColumns,
  getCellValue,
  hasFocus,
  rowIDKey,
  ixJCd,
  jid,
  handleReloadTimelogs
}) => {
  const { portal = {} } = useSelector(state => state.auth);
  const { token } = portal;
  const notify = useNotif();

  const request = useRequest();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openCell, setOpenCell] = useState(false);
  const [pending, setPending] = useState(false);
  const [approved, setApproved] = useState(false);
  const [openSubmit, setOpenSubmit] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [hrRemarks, setHrRemarks] = useState('');
  const [selectedTime, setSelectedTime] = useState({});

  const ITEMS = {
    HR_AM_IN: 'am IN',
    HR_AM_OUT: 'am OUT',
    HR_PM_IN: 'pm IN',
    HR_PM_OUT: 'pm OUT'
  };

  item = item || {};
  const logName = ITEMS[col.field];
  const isPM = logName?.startsWith('pm');

  const initialTime = item[col.field]
    ? parse(item[col.field], 'HH:mm', new Date())
    : new Date();

  const hours = initialTime?.getHours();
  const empRemarks = item?.EMP_remarks;
  const field = col.field;
  const hrField = field.slice(3);
  const hrValue = item?.[field];
  const empValue = item?.[`EMP_${hrField}`];
  const validTime = !isValid(selectedTime);

  const handleTimeChange = (field, time) => {
    if (!time || isNaN(time.getTime())) return;

    if (time === 'Invalid Date') {
      setApproved(false);
    }
    if (!time) return;

    const selectedFormatted = format(time, 'HH:mm');
    if (selectedFormatted !== hrValue) {
      setOpenSubmit(true);
    } else {
      setOpenSubmit(false);
    }

    setSelectedTime(prev => ({
      ...prev,
      [field]: time // only this cell's time is updated
    }));
  };

  const handleOpen = () => {
    setSelectedTime({
      [field]: parseTimeStringToDate(empValue || hrValue)
    });
    setOpenRequestLogs(true);
    setDialogOpen(true);
  };

  const handleOpenCell = () => {
    if (!portalShowEmpLogs) return;
    setOpenCell(true);
  };

  const handleSubmit = async () => {
    // Convert each selectedTime into HHMM number for payload
    const timePayload = Object.entries(
      selectedTime || parseTimeStringToDate(empValue)
    ).reduce((acc, [field, time]) => {
      if (!time) return acc;

      const hours = time.getHours();
      const minutes = time.getMinutes();
      const timeNumber = Number(
        String(hours).padStart(2, '0') + String(minutes).padStart(2, '0')
      );

      const cleanField = field.replace(/^HR_/, '');

      acc[cleanField] = timeNumber;
      return acc;
    }, {});

    const payload = [
      {
        ixDate: item?.ixDate,
        ...timePayload, // add all edited times
        remarks: remarks || empRemarks || prevRemarks
      }
    ];

    try {
      const res = await request.post(
        `/portal/hris/pr/update-bio/${jid}`,
        payload,
        {
          headers: {
            'x-access-tokens': token
          }
        }
      );

      if (!res?.success) {
        const message =
          res?.errorMessage ||
          res?.error?.response?.data?.msg ||
          'Failed to submit.';

        notify.error(message);
        return;
      }

      notify.success('Successfully updated.');

      setDialogOpen(false);
      setOpenRequestLogs(false);
      setPending(true);
      handleReloadTimelogs();
    } catch (error) {
      const message =
        error?.errorMessage ||
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        'Failed to submit.';

      notify.error(message);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setOpenRequestLogs(false);
    setOpenCell(false);
  };

  // TODO: Maybe sir Rado put a DELETE method here
  const handleReject = () => {
    setDialogOpen(false);
    setOpenRequestLogs(false);
  };

  // TODO: Add here the payload to submit an approve request
  const handleApprove = () => {
    const timeForField = selectedTime[field] || parseTimeStringToDate(empValue);

    const timeNumber = timeForField
      ? Number(
          String(timeForField.getHours()).padStart(2, '0') +
            String(timeForField.getMinutes()).padStart(2, '0')
        )
      : null;

    handleReqTime({
      ixDate: item?.ixDate,
      time: timeNumber,
      field: hrField,
      remarks: remarks || empRemarks
    });

    handleHrRemarks(hrRemarks);

    setApproved(true);
    setDialogOpen(false);
  };

  const onChangeField = e => {
    setPrevRemarks(e.target.value);
    setRemarks(e.target.value);
  };

  const onChangeHrRemarks = e => {
    setHrRemarks(e.target.value);
  };

  const HR_FIELDS = ['HR_AM_IN', 'HR_AM_OUT', 'HR_PM_IN', 'HR_PM_OUT'];
  const hrColumns = shownColumns?.filter(col => HR_FIELDS.includes(col.field));

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        onDoubleClick={handleOpen}
        sx={{ cursor: 'pointer' }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          marginLeft={1.5}
        >
          {getCellValue({
            col,
            row: item,
            id: item[rowIDKey],
            value:
              col?.type === 'sub' && col?.relatedTitleField
                ? item?.[col.relatedTitleField]
                : item[col.field],
            field: col.field,
            hasFocus
          })}
        </Box>

        <IconButton
          size="small"
          color="primary"
          sx={{ width: 32, height: 32, color: 'info.main', ml: 1 }}
          onClick={handleOpen}
        >
          {portalShowEmpLogs ? (
            pending ? (
              <HourglassEmptyIcon fontSize="small" />
            ) : empValue ? (
              empValue === hrValue ? (
                <CheckOutlinedIcon fontSize="small" />
              ) : (
                <HourglassEmptyIcon fontSize="small" />
              )
            ) : (
              <Box sx={{ width: 20, height: 20 }} />
            )
          ) : hrValue !== empValue ? (
            item?.EMP_remarks && !empValue ? null : value ? (
              empValue && value !== empValue && <PersonIcon fontSize="small" />
            ) : (
              <PersonIcon fontSize="small" />
            )
          ) : null}
        </IconButton>
      </Box>

      <Dialog
        fullWidth
        maxWidth="md"
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="max-width-dialog-title"
      >
        <DialogTitle id="max-width-dialog-title">
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="h4">
              Request{portalShowEmpLogs === 'admin' && 'ed'} Logs For{' '}
              {formatDate(item?.ixDate)} - {logName}
            </Typography>
          </Box>

          <Box mb={2} mt={2}>
            <Divider />
          </Box>

          <TableContainer component={Paper}>
            <Table aria-label="hr table">
              <TableHead>
                <TableRow>
                  {hrColumns?.map(colItem => (
                    <TableCell
                      key={colItem.field}
                      style={{ minWidth: colItem.minWidth || 100 }}
                    >
                      {colItem.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {hrColumns?.map(colItem => {
                    const isEditable = colItem?.field === field; // only current field is editable
                    const cellValue = item[colItem.field];

                    const getTime = getCellValue({
                      col: colItem,
                      row: item,
                      id: item[rowIDKey],
                      value: cellValue,
                      field: colItem.field,
                      hasFocus
                    });

                    return (
                      <TableCell key={colItem.field}>
                        {isEditable ? (
                          <>
                            <SelectTime
                              mode={1}
                              // value={
                              //   selectedTime[colItem.field] ||
                              //   parseTimeStringToDate(empValue) ||
                              //   parseTimeStringToDate(cellValue)
                              // }
                              value={selectedTime[colItem.field] || null}
                              onChange={time =>
                                handleTimeChange(colItem.field, time)
                              }
                              disabled={pending || !portalShowEmpLogs}
                            />
                          </>
                        ) : (
                          <IconButton onDoubleClick={handleOpenCell}>
                            {!openCell ? (
                              <TableCell
                                style={{
                                  color:
                                    pending || !portalShowEmpLogs
                                      ? 'gray'
                                      : 'inherit',
                                  pointerEvents:
                                    pending || !portalShowEmpLogs
                                      ? 'none'
                                      : 'auto',
                                  whiteSpace: 'nowrap', // prevents wrapping
                                  overflow: 'hidden', // hides overflowing content
                                  textOverflow: 'ellipsis', // adds "..." for overflow
                                  maxWidth: '200px' // optional: set a width limit
                                }}
                              >
                                {getTime}
                              </TableCell>
                            ) : (
                              <SelectTime
                                mode={1}
                                // value={
                                //   selectedTime[colItem.field] ||
                                //   parseTimeStringToDate(empValue) ||
                                //   parseTimeStringToDate(cellValue)
                                // }
                                value={selectedTime[colItem.field] || null}
                                onChange={time =>
                                  handleTimeChange(colItem.field, time)
                                }
                                disabled={pending || !portalShowEmpLogs}
                              />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogTitle>

        <DialogContent>
          {!portalShowEmpLogs ? (
            <>
              <TextField
                variant="outlined"
                fullWidth
                value={remarks || empRemarks}
                placeholder="Remarks..."
                onChange={onChangeField}
                disabled
                multiline
                minRows={3}
                maxRows={10}
              />

              <TextField
                variant="outlined"
                fullWidth
                value={hrRemarks}
                placeholder="HR Remarks..."
                onChange={onChangeHrRemarks}
                multiline
                minRows={3}
                style={{ marginTop: 7 }}
                maxRows={10}
              />

              <DialogActions>
                <Button onClick={handleReject} variant="contained">
                  Close
                </Button>
                <Button
                  onClick={handleApprove}
                  color="primary"
                  variant="contained"
                  disabled={!hrRemarks}
                >
                  Approve
                </Button>
              </DialogActions>
            </>
          ) : (
            <>
              <TextField
                variant="outlined"
                fullWidth
                value={remarks || empRemarks || prevRemarks}
                placeholder="Remarks..."
                onChange={onChangeField}
                multiline
                minRows={3}
                maxRows={20}
                disabled={pending || empRemarks}
              />

              <DialogActions>
                <Button onClick={handleCancel} variant="contained">
                  Close
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="primary"
                  variant="contained"
                  disabled={
                    !openSubmit ||
                    isValid(selectedTime) ||
                    (!remarks && !empRemarks && !prevRemarks)
                  }
                >
                  Submit
                </Button>
              </DialogActions>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequestLogs;
